import { useState, type ComponentProps, type ReactNode } from 'react';
import { Campo } from './Campo.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { Modal } from './Modal.js';
import { useT } from '../i18n/index.js';

/**
 * Lo que reemplaza a `confirm()` y `prompt()` del navegador.
 *
 * Those were reaching four places: voiding an expense, cancelling an order, paying a supplier,
 * deactivating a client. On a phone the native versions are unstyled system boxes that look
 * like the browser warning you rather than your own app asking, they sit wherever the OS puts
 * them, and `prompt()` cannot ask for a number pad — so the one that wanted a córdoba amount
 * opened a full QWERTY keyboard at a counter.
 *
 * They also block the JavaScript thread outright, which on a page driven by live Dexie queries
 * means the screen behind the box freezes mid-update.
 */
export function Confirmar({
  titulo,
  mensaje,
  confirmar,
  icono = 'confirmar',
  peligro = false,
  onConfirmar,
  onCerrar,
}: {
  titulo: string;
  mensaje: string;
  /** The button's words say what will happen. "Sí" makes somebody re-read the question. */
  confirmar: string;
  /** The picture of what will happen, for the same reason the words say it. */
  icono?: NombreIcono;
  peligro?: boolean;
  onConfirmar: () => void;
  onCerrar: () => void;
}) {
  const t = useT();
  return (
    <Modal
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        <div className="flex gap-3">
          <button type="button" className="btn-secundario flex-1" onClick={onCerrar}>
            <Ico icono="cancelar" />
            {t('comun.cancelar')}
          </button>
          <button
            type="button"
            className={`flex-1 ${peligro ? 'btn-peligro' : 'btn-primario'}`}
            onClick={() => {
              onConfirmar();
              onCerrar();
            }}
          >
            <Ico icono={icono} />
            {confirmar}
          </button>
        </div>
      }
    >
      <p className="text-sm text-slate-300">{mensaje}</p>
    </Modal>
  );
}

/**
 * One value, asked for properly: a real label, the right keyboard, and Enter to accept.
 *
 * `obligatorio` is the default because every current caller is recording *why* something was
 * undone, and a blank reason in the bitácora is the same as no bitácora.
 */
export function PedirDato({
  titulo,
  etiqueta,
  ayuda,
  placeholder,
  inputMode = 'text',
  maxLength,
  confirmar,
  icono = 'confirmar',
  onListo,
  onCerrar,
  validar,
  children,
}: {
  titulo: string;
  etiqueta: string;
  ayuda?: string;
  placeholder?: string;
  inputMode?: ComponentProps<typeof Campo>['inputMode'];
  /** As long as what it is kept in takes: past it, the record would be refused. */
  maxLength?: number;
  confirmar: string;
  icono?: NombreIcono;
  onListo: (valor: string) => void;
  onCerrar: () => void;
  /** Why the value will not do, said under the field instead of closing; null when it will. */
  validar?: (valor: string) => string | null;
  /** Anything else the answer depends on, below the field. */
  children?: ReactNode;
}) {
  const t = useT();
  const [valor, setValor] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listo = valor.trim().length > 0;

  function aceptar() {
    if (!listo) return;
    const problema = validar?.(valor.trim()) ?? null;
    setError(problema);
    if (problema) return;
    onListo(valor.trim());
    onCerrar();
  }

  return (
    <Modal
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        <div className="flex gap-3">
          <button type="button" className="btn-secundario flex-1" onClick={onCerrar}>
            <Ico icono="cancelar" />
            {t('comun.cancelar')}
          </button>
          <button type="button" className="btn-primario flex-1" disabled={!listo} onClick={aceptar}>
            <Ico icono={icono} />
            {confirmar}
          </button>
        </div>
      }
    >
      <Campo
        etiqueta={etiqueta}
        {...(ayuda ? { ayuda } : {})}
        {...(placeholder ? { placeholder } : {})}
        inputMode={inputMode}
        {...(maxLength ? { maxLength } : {})}
        className={inputMode === 'decimal' ? 'tabular text-lg' : undefined}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && aceptar()}
        autoFocus
        autoComplete="off"
        error={error}
      />
      {children}
    </Modal>
  );
}
